import type { SetupWizardStepId } from '../redux/ownerActivationSlice';

/**
 * Pure Back-navigation and reset decisions for the setup wizard (US4, T062).
 *
 * These functions describe *what should happen* on Back/Finish/Skip without
 * touching React Navigation, hardware Back, or browser history — the host wires
 * the returned decision to the real navigator. This keeps the deterministic
 * rules unit-testable and free of platform-API claims.
 */

export type OwnerSetupBackDecision =
  | { kind: 'noop' }
  | { kind: 'step_back'; toStepId: SetupWizardStepId }
  | { kind: 'replay_step_back'; toStepId: SetupWizardStepId }
  | { kind: 'replay_exit' };

export interface OwnerSetupBackInput {
  isReplay: boolean;
  stepIndex: number;
  steps: readonly SetupWizardStepId[];
}

export const decideOwnerSetupBack = (
  input: OwnerSetupBackInput,
): OwnerSetupBackDecision => {
  const { isReplay, stepIndex, steps } = input;
  if (isReplay) {
    if (stepIndex > 0) {
      return { kind: 'replay_step_back', toStepId: steps[stepIndex - 1] };
    }
    return { kind: 'replay_exit' };
  }
  // Initial run: Step 1 has no Back so auth/Home history stays unreachable.
  if (stepIndex > 0) {
    return { kind: 'step_back', toStepId: steps[stepIndex - 1] };
  }
  return { kind: 'noop' };
};

/**
 * The navigation reset used by Finish and Skip so Back cannot reopen the
 * initial setup. Returned as a plain object the host passes to
 * `navigation.reset(...)`.
 */
export const ownerSetupHomeResetAction = () => ({
  index: 0,
  routes: [
    { name: 'DrawerNavigation' as const, params: { screen: 'Home' as const } },
  ],
});
